import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Auth } from '@freelancer/auth';
import { map } from 'rxjs/operators';
import { TRACKING_CONFIG } from './tracking.config';
import { TrackingConfig } from './tracking.interface';

/*
 * This uses Google's global site tag (gtag.js) snippet, which replaced the old
 * Universal Analytics (analytics.js) and other product-specific SDKs and
 * allows to communicate to Google Analytics, Google Ads, and Google Marketing
 * Platform.
 */
@Injectable()
export class GoogleTracking {
  isEnabled = false;

  constructor(
    private auth: Auth,
    @Inject(TRACKING_CONFIG) private config: TrackingConfig,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {
    if (this.config.gaTrackingId && isPlatformBrowser(this.platformId)) {
      this.isEnabled = true;
      window.dataLayer = window.dataLayer || [];
    }
  }

  trackPageView(): void {
    if (!this.isEnabled) {
      return;
    }
    this.auth.authState$
      .pipe(
        map(auth => {
          this.gtag('config', this.config.gaTrackingId, {
            user_id: auth ? auth.userId : undefined,
            page_location: window.location.href,
            page_path: window.location.pathname,
          });
        }),
      )
      .toPromise();
  }

  private gtag(...args: any[]): void {
    this.loadTrackingSnippet();
    // We do need arguments here as GA does weird things with it, e.g. probably
    // reading the caller/callee or something, as just passing args doesn't
    // work (silentely fails & nothing is tracked).
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments);
  }

  private loadTrackingSnippet(): void {
    if (!document.getElementById('gtag-sdk')) {
      const e = document.createElement('script');
      e.type = 'text/javascript';
      e.src = `https://www.googletagmanager.com/gtag/js?id=${
        this.config.gaTrackingId
      }`;
      e.id = 'gtag-sdk';
      const s = document.getElementsByTagName('script')[0];
      (s.parentNode as Node).insertBefore(e, s);
      this.gtag('js', new Date());
      this.gtag('config', this.config.gaTrackingId, {
        // automatic page view tracking is disabled as we manually send page
        // view events to track the SPA route changes
        send_page_view: false,
        // use navigator.sendBeacon when supported. will be the default in a
        // future release.
        transport_type: 'beacon',
      });
      // Enable AdWords conversion tracking and remarketing
      if (this.config.awGoogleConversionId) {
        this.gtag('config', this.config.awGoogleConversionId);
      }
    }
  }
}
